import axios from 'axios';
import Analytics from '@web-solutions/module-analytics';

import { getEnvUrl } from './getEnvUrl';
import { getPwaUrlForOnboarding } from './getPwaUrl';

/**
 * Exchanges the funnel's accessToken for a one-time handoff code and builds the
 * PWA url with that code in the fragment, so the user lands in the app already
 * logged in — without a second email code.
 *
 * The code lives 5 minutes and is single-use, so it must be requested at click
 * time, not in advance. Calling the endpoint again is safe: it issues a new code
 * and kills the previous one, so retries and repeated clicks keep working.
 *
 * Autologin is best effort — on any failure we return the plain PWA url and the
 * user goes through the usual Welcome/OTP flow.
 */
export const buildPwaHandoffUrl = async (accessToken?: string): Promise<string> => {
  const pwaUrl = getPwaUrlForOnboarding();

  if (!accessToken) {
    return pwaUrl;
  }

  const { apiBaseUrl } = getEnvUrl();

  try {
    const response = await axios.post(`${apiBaseUrl}/auth/handoff`, undefined, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const code = response?.data?.code;

    if (!code) {
      Analytics.trackEvent('funnel_autologin', 'error');
      return pwaUrl;
    }

    Analytics.trackEvent('funnel_autologin', 'success');

    // Strictly a fragment (`#handoff=`), never a query param: the fragment is
    // not sent to the server, so it stays out of CDN logs and `Referer`.
    return `${pwaUrl}/#handoff=${encodeURIComponent(code)}`;
  } catch (error) {
    Analytics.trackEvent('funnel_autologin', 'error');
    console.error('PWA handoff: failed to get code', error);
    return pwaUrl;
  }
};
